import { useEffect, useState } from "react";
import {
  createPlaylist,
  deletePlaylist,
  listPlaylists,
} from "../lib/resources.ts";
import type { Playlist } from "../lib/types.ts";
import PlaylistEditor from "./PlaylistEditor.tsx";

// Playlists — reusable ordered loops bundling content with per-item durations.
export default function Playlists() {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  /** Set when we just created the playlist, so the editor focuses the name field. */
  const [isNew, setIsNew] = useState(false);
  const [creating, setCreating] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      setPlaylists(await listPlaylists());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load playlists");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  /**
   * Create opens the new playlist straight away rather than asking for a name
   * up front — naming happens in the editor, next to the content you're adding.
   * The placeholder name is replaced there; it only persists if someone saves
   * without renaming.
   */
  async function create() {
    setCreating(true);
    setError(null);
    try {
      const p = await createPlaylist("Untitled playlist");
      await refresh();
      setIsNew(true);
      setOpenId(p.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn’t create the playlist");
    } finally {
      setCreating(false);
    }
  }

  async function remove(p: Playlist) {
    if (!confirm(`Delete playlist “${p.name}”?`)) return;
    await deletePlaylist(p.id);
    await refresh();
  }

  if (openId) {
    return (
      <PlaylistEditor
        playlistId={openId}
        autoFocusName={isNew}
        onClose={() => {
          setOpenId(null);
          setIsNew(false);
          void refresh();
        }}
      />
    );
  }

  return (
    <section className="admin-page">
      <div className="page-head">
        <div>
          <h1>Playlists</h1>
          <p className="muted">
            Ordered loops (e.g. “Sunday Rotation”) you can drop onto any schedule.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={create} disabled={creating}>
            {creating ? "Creating…" : "+ Create playlist"}
          </button>
        </div>
      </div>

      {error && <div className="banner banner--error">{error}</div>}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : playlists.length === 0 ? (
        <div className="admin-empty">
          <strong>No playlists yet</strong>
          <span>Hit “Create playlist” — you can name it on the next screen.</span>
        </div>
      ) : (
        <div className="list">
          {playlists.map((p) => (
            <div className="list-row card" key={p.id}>
              <button className="list-row-main" onClick={() => setOpenId(p.id)}>
                <span className="list-row-name">{p.name}</span>
                <span className="muted">
                  {p.itemCount} item{p.itemCount === 1 ? "" : "s"}
                </span>
              </button>
              <div className="list-row-actions">
                <button className="link-btn" onClick={() => setOpenId(p.id)}>
                  Edit
                </button>
                <button
                  className="link-btn link-btn--danger"
                  onClick={() => remove(p)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
